/* eslint-disable @typescript-eslint/no-floating-promises */
import React from 'react';
import { useNavigate } from 'react-router';
import { EquipmentStates } from '../../../../common/src/enums/EquipmentStates';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson } from '../../store/fetch';
import { openApiFetchDelete, openApiFetchGet } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { SortColumn, SortDirections } from '../../utils/sorting';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelInput,
    EditPanelSection,
    EditPanelSelect,
    ISelectOption,
    PanelBody,
    PanelLink,
    PanelRouterLink,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { EquipmentExpirationLabel } from '../utils/equipmentExpirationLabel';
import { ListPager } from '../utils/ListPager';
import { NullableBoolean } from '../utils/NullableBoolean';
import { useReactAsync } from '../utils/useReactAsync';
import { useSearch } from '../utils/useSearch';

interface ISearchAttributes {
    status?: string;
    equipmentTypeId?: string;
    clientCompanyId?: string;
    name?: string;
}

async function loadEquipment(searchParameters: ISearchAttributes & { limit: string; offset: string; sortColumns: SortColumn[] }) {
    const result = await openApiFetchGet('/api/equipment', searchParameters);
    return result;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function EquipmentList() {
    const translate = useTranslate();
    const navigate = useNavigate();

    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);

    const [equipmentTypes, setEquipmentTypes] = React.useState<ISelectOption[]>([]);
    const [clientCompanies, setClientCompanies] = React.useState<ISelectOption[]>([]);

    const { searchParameters, handleSortChanged, handleNavigation, setSearchAttribute, resetSearch, queryObject, itemsPerPage } =
        useSearch<ISearchAttributes>({}, [{ columnName: 'name', direction: SortDirections.Ascending }], undefined, 'EquipmentList');

    const [equipmentName, setEquipmentName] = React.useState(searchParameters.name as unknown as string);
    const { data, reload, isLoading } = useReactAsync(loadEquipment, queryObject, [queryObject]);

    async function removeEquipment(equipmentId: string) {
        await openApiFetchDelete('/api/equipment/{equipmentId}', { equipmentId });
        await reload();
    }

    React.useEffect(() => {
        (async () => {
            const responseEquipmentTypes = await openApiFetchGet('/api/equipmentTypes', {});
            if (responseEquipmentTypes) {
                setEquipmentTypes(responseEquipmentTypes.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
            }
            const responseClientCompanies = await fetchJson('/api/clients', 'GET');
            setClientCompanies(
                responseClientCompanies.json.items.map((company: { id: string; name: string }) => ({ text: company.name, value: company.id })),
            );
        })();
    }, []);

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={translate('equipment.management')} icon="fa-briefcase" />
                </TopbarLeft>
                <TopbarRight>
                    <PanelLink
                        href={'/equipment/flightLogExport'}
                        text={translate('equipment.flightLogExport')}
                        icon="fa fa-download"
                        isDownload
                        small
                    />
                    <PanelRouterLink
                        to="/equipment/add"
                        text={translate('common.addItem', translate('equipment.equipment'))}
                        icon="fa fa-plus"
                        small={true}
                    />
                </TopbarRight>
            </Topbar>

            <Content>
                <Tray>
                    <PanelWithoutForm>
                        {(isLoading && <Spinner />) || (
                            <PanelBody noPadding={true}>
                                <Table hover>
                                    <TableHead>
                                        <TableHeadRow light={true}>
                                            <TableSortableHeader
                                                header={translate('common.name')}
                                                columnName="name"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            <TableSortableHeader
                                                header={translate('common.type')}
                                                columnName="equipmentType.name"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            {isAdmin && (
                                                <>
                                                    <TableSortableHeader
                                                        header={translate('common.company')}
                                                        columnName="clientCompany.name"
                                                        sortColumns={searchParameters.sortColumns}
                                                        onClick={handleSortChanged}
                                                    />
                                                </>
                                            )}
                                            <TableSortableHeader
                                                header={translate('equipment.payload')}
                                                columnName="equipmentType.isPayload"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            <TableSortableHeader
                                                header={translate('common.status')}
                                                columnName="status"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            <TableSortableHeader
                                                header={translate('equipment.expirationDate')}
                                                columnName="dateEndCertificateValidity"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            <TableHeader>&nbsp;</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {data &&
                                            data.items.map((item) => (
                                                <TableRow key={item.id} onClick={() => navigate(`/equipment/${item.id}`)}>
                                                    <TableData> {item.name} </TableData>
                                                    <TableData> {item.equipmentType.name} </TableData>
                                                    {isAdmin && (
                                                        <>
                                                            <TableData>{item.clientCompany.name}</TableData>
                                                        </>
                                                    )}
                                                    <TableData>
                                                        {' '}
                                                        <NullableBoolean value={item.equipmentType.isPayload} />{' '}
                                                    </TableData>
                                                    <TableData> {translate(`equipment.${item.status}`)}</TableData>
                                                    <TableData alignRight>
                                                        {' '}
                                                        <EquipmentExpirationLabel data={item} />
                                                    </TableData>
                                                    <TableButtons>
                                                        <IconEditButton onClick={() => navigate(`/equipment/${item.id}/edit`)} />
                                                        <IconRemoveButton
                                                            removeQuestion={translate('common.confirmDelete', item.name)}
                                                            onClick={async () => removeEquipment(item.id)}
                                                        />
                                                    </TableButtons>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </PanelBody>
                        )}
                        <ListPager
                            disabled={isLoading}
                            itemsPerPage={itemsPerPage}
                            total={data?.total ?? 0}
                            page={searchParameters.page}
                            handleNavigation={handleNavigation}
                        />
                    </PanelWithoutForm>
                </Tray>
                <RightTray>
                    <form
                        id="filter"
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <div className="admin-form">
                            <h4> {translate('common.findItem', translate('equipment.equipment'))}</h4>
                            <EditPanelSection topBorder={true}>
                                <EditPanelInput
                                    name="equipmentName"
                                    placeholder={translate('common.name')}
                                    value={equipmentName}
                                    icon="fa fa-briefcase"
                                    handleChange={(value) => setEquipmentName(value)}
                                    handleEnter={() => setSearchAttribute({ name: equipmentName })}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelSelect
                                    name="equipmentStatus"
                                    value={searchParameters.status}
                                    handleChange={(value) => setSearchAttribute({ status: value })}
                                    disabled={isLoading}
                                    placeholder={translate('common.selectItem', translate('common.status'))}
                                    options={[
                                        ...[
                                            { value: EquipmentStates.InUse, text: translate(`equipment.${EquipmentStates.InUse}`) },
                                            { value: EquipmentStates.InMaintenance, text: translate(`equipment.${EquipmentStates.InMaintenance}`) },
                                            { value: EquipmentStates.OutOfService, text: translate(`equipment.${EquipmentStates.OutOfService}`) },
                                            {
                                                value: EquipmentStates.UnderConstruction,
                                                text: translate(`equipment.${EquipmentStates.UnderConstruction}`),
                                            },
                                        ],
                                        ...(isAdmin
                                            ? [{ value: EquipmentStates.Deleted, text: translate(`equipment.${EquipmentStates.Deleted}`) }]
                                            : []),
                                    ]}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelSelect
                                    name="equipmentTypeId"
                                    value={searchParameters.equipmentTypeId}
                                    handleChange={(value) => setSearchAttribute({ equipmentTypeId: value })}
                                    disabled={isLoading}
                                    placeholder={translate('common.selectItem', translate('equipment.types'))}
                                    options={equipmentTypes}
                                />
                            </EditPanelSection>
                            {isAdmin && (
                                <EditPanelSection>
                                    <EditPanelSelect
                                        name="clientCompanyId"
                                        value={searchParameters.clientCompanyId}
                                        handleChange={(value) => setSearchAttribute({ clientCompanyId: value })}
                                        disabled={isLoading}
                                        placeholder={translate('common.selectItem', translate('common.client'))}
                                        options={clientCompanies}
                                    />
                                </EditPanelSection>
                            )}
                            <EditPanelButtons topBorder>
                                <EditPanelButton
                                    onClick={() => {
                                        setEquipmentName('');
                                        resetSearch();
                                    }}
                                    text={translate('common.reset')}
                                    disabled={isLoading}
                                />
                                <EditPanelButton
                                    onClick={() => setSearchAttribute({ name: equipmentName })}
                                    text={translate('common.search')}
                                    primary
                                    disabled={isLoading}
                                />
                            </EditPanelButtons>
                        </div>
                    </form>
                </RightTray>
            </Content>
        </>
    );
}
